// Platform admin — Sinch settings + quotas.
// Route : /app/platform/communications (accessible uniquement aux
// platform admins — détecté côté backend via PLATFORM_ADMIN_EMAILS).

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, Loader2, ShieldCheck, ShieldOff, X, AlertCircle,
  CheckCircle2, Send, Save, MessageSquare, Users2, Activity, Zap,
  FileText, BarChart3,
} from "lucide-react";
import api from "@/lib/api";
import {
  SmsTemplatesTab, SmsKpiTab, SmsJournalFilters,
} from "@/pages/platform/SmsTemplatesPanel";
import {
  ProvidersAdminTab, AnalyticsTab,
} from "@/pages/platform/CommunicationRouterPanel";

const PlatformSinchPage = () => {
  const [tab, setTab] = useState("sinch");
  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-10 py-10" data-testid="page-platform-sinch">
      <Link to="/app/parametres"
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#6B7280] hover:text-[#111]">
        <ChevronLeft size={14} /> Retour aux paramètres
      </Link>
      <div className="mt-4 flex items-center justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-[#6B7280]">Plateforme wyvio</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-[-0.02em] text-[#111]">
            Communications Sinch
          </h1>
          <p className="mt-1.5 text-[14px] text-[#6B7280]">
            Configuration technique du provider SMS Sinch, modèles, quotas, journal et KPI.
            Réservé aux administrateurs plateforme.
          </p>
        </div>
      </div>

      <div className="mt-8 flex items-center gap-1 border-b border-black/5 overflow-x-auto">
        <TabBtn active={tab === "sinch"} onClick={() => setTab("sinch")} testId="tab-sinch">
          <MessageSquare size={14} /> Provider Sinch
        </TabBtn>
        <TabBtn active={tab === "providers"} onClick={() => setTab("providers")} testId="tab-providers">
          <ShieldCheck size={14} /> Providers
        </TabBtn>
        <TabBtn active={tab === "templates"} onClick={() => setTab("templates")} testId="tab-templates">
          <FileText size={14} /> Modèles SMS
        </TabBtn>
        <TabBtn active={tab === "kpi"} onClick={() => setTab("kpi")} testId="tab-kpi">
          <BarChart3 size={14} /> KPI SMS
        </TabBtn>
        <TabBtn active={tab === "analytics"} onClick={() => setTab("analytics")} testId="tab-analytics">
          <Zap size={14} /> Analytics
        </TabBtn>
        <TabBtn active={tab === "quotas"} onClick={() => setTab("quotas")} testId="tab-quotas">
          <Users2 size={14} /> Quotas agences
        </TabBtn>
        <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} testId="tab-messages">
          <Activity size={14} /> Journal SMS
        </TabBtn>
      </div>

      <div className="mt-8">
        {tab === "sinch"     && <SinchSettingsCard />}
        {tab === "providers" && <ProvidersAdminTab />}
        {tab === "templates" && <SmsTemplatesTab />}
        {tab === "kpi"       && <SmsKpiTab />}
        {tab === "analytics" && <AnalyticsTab />}
        {tab === "quotas"    && <QuotasCard />}
        {tab === "messages"  && <MessagesCard />}
      </div>
    </div>
  );
};

const TabBtn = ({ active, onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId}
          className={`inline-flex items-center gap-2 h-11 px-4 text-[13px] font-semibold
            border-b-2 -mb-px transition-colors
            ${active ? "border-[#111] text-[#111]" : "border-transparent text-[#6B7280] hover:text-[#111]"}`}>
    {children}
  </button>
);

// ---------- Sinch settings ----------

const SinchSettingsCard = () => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");
  const [showTestModal, setShowTestModal] = useState(false);
  const [showLiveTestModal, setShowLiveTestModal] = useState(false);

  // Editable form fields
  const [projectId, setProjectId] = useState("");
  const [appId, setAppId] = useState("");
  const [accessKeyId, setAccessKeyId] = useState("");
  const [accessKeySecret, setAccessKeySecret] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [sender, setSender] = useState("");
  const [region, setRegion] = useState("eu");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const s = await api.platformGetSinch();
      setSettings(s);
      setProjectId(s.project_id || "");
      setAppId(s.app_id || "");
      setSender(s.sender || "WYVIO");
      setRegion(s.region || "eu");
      setActive(s.active);
    } catch (e) {
      setError(e.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      await api.platformPatchSinch({
        project_id: projectId || null,
        app_id: appId || null,
        access_key_id: accessKeyId || null,        // "" garde inchangé
        access_key_secret: accessKeySecret || null,
        webhook_secret: webhookSecret || null,
        sender: sender || null,
        region,
        active,
      });
      setAccessKeyId(""); setAccessKeySecret(""); setWebhookSecret("");
      await load();
    } catch (err) {
      setError(err.message || "Erreur");
    } finally { setSaving(false); }
  };

  if (loading) return <Loader2 size={20} className="animate-spin" />;
  if (!settings) return null;

  const status = settings.verified ? "Vérifié" : settings.configured ? "Configuré" : "Non configuré";
  const statusBg = settings.verified ? "bg-[#D9FFE8] text-[#166534]" : settings.configured ? "bg-[#DBEAFE] text-[#1E40AF]" : "bg-[#F1F5F9] text-[#475569]";

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-black/5 bg-white p-6" data-testid="platform-sinch-status">
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusBg}`}>
            {settings.verified ? <CheckCircle2 size={11} /> : <ShieldOff size={11} />}
            {status}
          </span>
          {settings.active === false && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#FEF3C7] text-[#92400E] px-2 py-0.5 text-[11px] font-semibold">
              Désactivé
            </span>
          )}
        </div>
        <div className="mt-4 rounded-xl bg-[#F7F8FA] p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <KV k="Project ID" v={settings.project_id} testid="platform-project-id" />
          <KV k="App ID" v={settings.app_id} testid="platform-app-id" />
          <KV k="Access Key ID" v={settings.access_key_id_masked} testid="platform-access-key-id" />
          <KV k="Access Key Secret" v={settings.access_key_secret_masked} testid="platform-access-key-secret" />
          <KV k="Webhook Secret" v={settings.webhook_secret_masked} testid="platform-webhook-secret" />
          <KV k="Sender ID" v={settings.sender} testid="platform-sender" />
          <KV k="Region" v={settings.region} testid="platform-region" />
          <KV k="Source" v={settings.source} testid="platform-source" />
          {settings.last_test_at && (
            <KV k="Dernier test"
                v={new Date(settings.last_test_at).toLocaleString("fr-FR") + " · " + (settings.last_test_status || "")}
                testid="platform-last-test" />
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => setShowTestModal(true)}
                  disabled={!settings.configured || !settings.active}
                  data-testid="platform-test-sinch-btn"
                  className="h-9 inline-flex items-center gap-2 rounded-full bg-white border border-black/10 px-4 text-[12.5px] font-medium text-[#111] hover:bg-[#F7F8FA] disabled:opacity-50">
            <Send size={13} /> Test rapide (auth)
          </button>
          <button onClick={() => setShowLiveTestModal(true)}
                  disabled={!settings.configured || !settings.active}
                  data-testid="platform-livetest-sinch-btn"
                  className="h-9 inline-flex items-center gap-2 rounded-full bg-[#111] text-white px-4 text-[12.5px] font-medium hover:bg-black disabled:opacity-50">
            <Zap size={13} /> Live test tracé (1/60s)
          </button>
        </div>
      </section>

      <form onSubmit={save} className="rounded-2xl border border-black/5 bg-white p-6 space-y-4"
            data-testid="platform-sinch-form">
        <h3 className="text-[15px] font-semibold text-[#111]">Configuration Sinch</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Project ID">
            <input value={projectId} onChange={(e) => setProjectId(e.target.value)}
                   className="input" data-testid="input-project-id" required />
          </Field>
          <Field label="App ID (Service Plan)">
            <input value={appId} onChange={(e) => setAppId(e.target.value)}
                   className="input" data-testid="input-app-id" required />
          </Field>
          <Field label="Access Key ID">
            <input value={accessKeyId} onChange={(e) => setAccessKeyId(e.target.value)}
                   placeholder={settings.access_key_id_masked ? "Laisser vide pour conserver" : "KEY_..."}
                   className="input font-mono" data-testid="input-access-key-id" />
          </Field>
          <Field label="Access Key Secret">
            <input type="password" value={accessKeySecret}
                   onChange={(e) => setAccessKeySecret(e.target.value)}
                   placeholder={settings.access_key_secret_masked ? "Laisser vide pour conserver" : "SECRET_..."}
                   className="input font-mono" data-testid="input-access-key-secret" />
          </Field>
          <Field label="Webhook Secret (Bearer token)">
            <input type="password" value={webhookSecret}
                   onChange={(e) => setWebhookSecret(e.target.value)}
                   placeholder={settings.webhook_secret_masked ? "Laisser vide pour conserver" : "whsec_..."}
                   className="input font-mono" data-testid="input-webhook-secret" />
          </Field>
          <Field label="Sender ID (alphanumérique)">
            <input value={sender} onChange={(e) => setSender(e.target.value)}
                   maxLength={11} className="input font-mono" data-testid="input-sender" />
          </Field>
          <Field label="Region">
            <select value={region} onChange={(e) => setRegion(e.target.value)}
                    className="input bg-white" data-testid="input-region">
              <option value="eu">Europe</option>
              <option value="us">United States</option>
            </select>
          </Field>
          <Field label="Actif">
            <label className="flex items-center gap-2 h-11">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)}
                     data-testid="input-active" />
              <span className="text-[13px] text-[#111]">Fournisseur Sinch activé</span>
            </label>
          </Field>
        </div>
        {error && <div className="rounded-lg bg-[#FEE2E2] text-[#991B1B] text-[12.5px] px-3 py-2">{error}</div>}
        <button type="submit" disabled={saving}
                data-testid="platform-save-sinch"
                className="h-11 inline-flex items-center gap-2 rounded-full bg-[#111] text-white text-[14px] font-medium px-6 hover:bg-black disabled:opacity-50">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
          Enregistrer
        </button>
      </form>

      <section className="rounded-2xl border border-black/5 bg-white p-6" data-testid="platform-webhook-block">
        <h3 className="text-[15px] font-semibold text-[#111]">URLs des webhooks Sinch</h3>
        <div className="mt-3 space-y-4">
          <div>
            <p className="text-[12.5px] font-semibold text-[#111]">Conversation API — Lot 17 (recommandé)</p>
            <p className="mt-1 text-[12.5px] text-[#6B7280] leading-relaxed">
              À configurer dans Sinch Dashboard → Conversation API → Apps → Webhook.
              Signature <strong>HMAC-SHA256</strong> via en-têtes
              <code className="mx-1 px-1 bg-[#F7F8FA] rounded">X-Sinch-Webhook-Signature</code>,
              <code className="mx-1 px-1 bg-[#F7F8FA] rounded">-Nonce</code>,
              <code className="mx-1 px-1 bg-[#F7F8FA] rounded">-Timestamp</code>.
            </p>
            <code className="mt-2 block text-[12px] font-mono bg-[#F7F8FA] p-3 rounded-lg break-all"
                  data-testid="platform-webhook-url-conversation">
              {window.location.origin}/api/webhooks/sinch/conversation
            </code>
          </div>
          <div>
            <p className="text-[12.5px] font-semibold text-[#111]">SMS XMS API — Legacy (fallback)</p>
            <p className="mt-1 text-[12.5px] text-[#6B7280] leading-relaxed">
              Ancien endpoint (Lot 16). Auth <code className="px-1 bg-[#F7F8FA] rounded">Authorization: Bearer {`{webhook_secret}`}</code>.
            </p>
            <code className="mt-2 block text-[12px] font-mono bg-[#F7F8FA] p-3 rounded-lg break-all"
                  data-testid="platform-webhook-url">
              {window.location.origin}/api/webhooks/sinch/status
            </code>
          </div>
        </div>
      </section>

      {showTestModal && (
        <TestSinchModal
          onClose={() => setShowTestModal(false)}
          onDone={load}
        />
      )}
      {showLiveTestModal && (
        <LiveTestSinchModal
          onClose={() => setShowLiveTestModal(false)}
          onDone={load}
        />
      )}
    </div>
  );
};

const TestSinchModal = ({ onClose, onDone }) => {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setResult(null);
    try {
      const r = await api.platformTestSinch(phone);
      setResult(r);
      await onDone?.();
    } catch (err) { setError(err.message || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Tester Sinch" onClose={onClose} testId="platform-test-modal">
      {!result ? (
        <form onSubmit={submit} className="mt-4 space-y-3">
          <p className="text-[13px] text-[#6B7280]">
            Un SMS unique va être envoyé au numéro fourni. Aucun événement workflow.
          </p>
          <Field label="Numéro mobile (FR ou E.164)">
            <input required value={phone} onChange={(e) => setPhone(e.target.value)}
                   placeholder="06 20 88 27 01" data-testid="platform-test-phone"
                   className="input font-mono" />
          </Field>
          {error && <div className="rounded-lg bg-[#FEE2E2] text-[#991B1B] text-[12.5px] px-3 py-2">{error}</div>}
          <button type="submit" disabled={busy || !phone}
                  data-testid="platform-test-submit"
                  className="w-full h-11 rounded-full bg-[#111] text-white text-[14px] font-medium">
            {busy ? <Loader2 size={15} className="animate-spin inline" /> : "Envoyer"}
          </button>
        </form>
      ) : (
        <div data-testid="platform-test-result"
             className={`mt-5 rounded-2xl p-4 border ${result.verified ? "bg-[#D9FFE8] border-[#86EFAC]" : "bg-[#FEE2E2] border-[#FCA5A5]"}`}>
          <div className="flex items-start gap-3">
            {result.verified ? <CheckCircle2 size={18} className="text-[#166534] mt-0.5" /> : <AlertCircle size={18} className="text-[#991B1B] mt-0.5" />}
            <div className="flex-1">
              <p className={`text-[14px] font-semibold ${result.verified ? "text-[#166534]" : "text-[#991B1B]"}`}>
                {result.verified ? "Connexion vérifiée" : "Échec"}
              </p>
              <p className="mt-1 text-[12.5px]">Destinataire : <b>{result.recipient}</b></p>
              <p className="text-[12.5px]">Provider : {result.provider}</p>
              {result.provider_id && (
                <p className="text-[11px] font-mono mt-1">Batch ID : {result.provider_id}</p>
              )}
              {result.error && (
                <p className="mt-2 text-[12px] text-[#991B1B] font-mono break-all">
                  Erreur : {result.error}
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose}
                  className="mt-4 w-full h-10 rounded-full bg-[#111] text-white text-[13px] font-medium">Fermer</button>
        </div>
      )}
    </ModalShell>
  );
};

// ---------- Quotas ----------

const QuotasCard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    const d = await api.platformListQuotas();
    setData(d);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (loading) return <Loader2 size={18} className="animate-spin" />;
  if (!data) return null;

  return (
    <section className="rounded-2xl border border-black/5 bg-white p-6" data-testid="platform-quotas">
      <h3 className="text-[15px] font-semibold text-[#111]">Consommation SMS · {data.period}</h3>
      <div className="mt-4 space-y-2">
        {data.agencies.map((a) => (
          <div key={a.agency_id}
               data-testid={`quota-row-${a.agency_id}`}
               className="flex items-center gap-4 rounded-2xl border border-black/5 px-4 py-3 hover:border-black/10 transition-colors">
            <div className="flex-1">
              <p className="text-[14px] font-semibold text-[#111]">{a.agency_name}</p>
              <p className="text-[11.5px] text-[#6B7280] font-mono">{a.agency_id}</p>
            </div>
            <div className="text-right">
              <p className="text-[15px] font-semibold text-[#111]">
                {a.used_sms}<span className="text-[12px] text-[#6B7280]"> / {a.included_sms + a.extra_sms}</span>
              </p>
              {a.over_quota && (
                <span className="text-[10px] uppercase tracking-widest text-[#B91C1C]">Dépassé</span>
              )}
            </div>
            <button onClick={() => setEditing(a)}
                    data-testid={`quota-edit-${a.agency_id}`}
                    className="h-8 px-3 rounded-full bg-[#F7F8FA] border border-black/5 text-[12px] font-medium hover:bg-white">
              Ajuster
            </button>
          </div>
        ))}
      </div>
      {editing && (
        <QuotaEditModal agency={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />
      )}
    </section>
  );
};

const QuotaEditModal = ({ agency, onClose, onSaved }) => {
  const [included, setIncluded] = useState(agency.included_sms);
  const [extra, setExtra] = useState(agency.extra_sms);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.platformSetQuota(agency.agency_id, {
        included_sms: Number(included),
        extra_sms: Number(extra),
      });
      await onSaved();
    } finally { setBusy(false); }
  };
  return (
    <ModalShell title={`Quota · ${agency.agency_name}`} onClose={onClose} testId="quota-edit-modal">
      <form onSubmit={submit} className="mt-4 space-y-3">
        <Field label="SMS inclus (base)">
          <input type="number" value={included} onChange={(e) => setIncluded(e.target.value)}
                 className="input" data-testid="quota-included-input" min="0" />
        </Field>
        <Field label="SMS supplémentaires accordés">
          <input type="number" value={extra} onChange={(e) => setExtra(e.target.value)}
                 className="input" data-testid="quota-extra-input" min="0" />
        </Field>
        <button type="submit" disabled={busy}
                data-testid="quota-save-submit"
                className="w-full h-11 rounded-full bg-[#111] text-white text-[14px] font-medium hover:bg-black">
          {busy ? <Loader2 size={15} className="animate-spin inline" /> : "Enregistrer"}
        </button>
      </form>
    </ModalShell>
  );
};

// ---------- Live test tracé (Lot 17) ----------

const LiveTestSinchModal = ({ onClose, onDone }) => {
  const [phone, setPhone] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setResult(null);
    try {
      const r = await api.platformLiveTestSinch(phone, body);
      setResult(r);
      await onDone?.();
    } catch (err) { setError(err.message || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Live test tracé Sinch" onClose={onClose} testId="platform-livetest-modal">
      {!result ? (
        <form onSubmit={submit} className="mt-4 space-y-3">
          <p className="text-[13px] text-[#6B7280]">
            Envoi live tracé dans le journal SMS (rate-limit 1/60s). Ne consomme pas le quota agence.
          </p>
          <Field label="Numéro mobile (FR ou E.164)">
            <input required value={phone} onChange={(e) => setPhone(e.target.value)}
                   placeholder="06 20 88 27 01" data-testid="platform-livetest-phone"
                   className="input font-mono" />
          </Field>
          <Field label="Message (optionnel)">
            <textarea value={body} onChange={(e) => setBody(e.target.value)}
                      maxLength={400} rows={3}
                      placeholder="Laisser vide pour message par défaut"
                      data-testid="platform-livetest-body"
                      className="input" />
          </Field>
          {error && <div className="rounded-lg bg-[#FEE2E2] text-[#991B1B] text-[12.5px] px-3 py-2">{error}</div>}
          <button type="submit" disabled={busy || !phone}
                  data-testid="platform-livetest-submit"
                  className="w-full h-11 rounded-full bg-[#111] text-white text-[14px] font-medium">
            {busy ? <Loader2 size={15} className="animate-spin inline" /> : "Envoyer live test"}
          </button>
        </form>
      ) : (
        <div data-testid="platform-livetest-result"
             className={`mt-5 rounded-2xl p-4 border ${result.verified ? "bg-[#D9FFE8] border-[#86EFAC]" : "bg-[#FEF3C7] border-[#FCD34D]"}`}>
          <div className="flex items-start gap-3">
            {result.verified ? <CheckCircle2 size={18} className="text-[#166534] mt-0.5" /> : <AlertCircle size={18} className="text-[#92400E] mt-0.5" />}
            <div className="flex-1">
              <p className={`text-[14px] font-semibold ${result.verified ? "text-[#166534]" : "text-[#92400E]"}`}>
                Statut : {result.status}
              </p>
              <p className="mt-1 text-[12.5px]">Destinataire : <b>{result.recipient}</b></p>
              <p className="text-[12.5px]">Segments : <b>{result.segments}</b> ({result.encoding})</p>
              <p className="text-[12.5px]">Tentatives : {result.attempts}</p>
              {result.sms_message_id && (
                <p className="text-[11px] font-mono mt-1">SMS ID : {result.sms_message_id}</p>
              )}
              {result.provider_id && (
                <p className="text-[11px] font-mono">Provider ID : {result.provider_id}</p>
              )}
              {result.error && (
                <p className="mt-2 text-[12px] text-[#B45309] font-mono break-all">
                  Erreur : {result.error}
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose}
                  className="mt-4 w-full h-10 rounded-full bg-[#111] text-white text-[13px] font-medium">Fermer</button>
        </div>
      )}
    </ModalShell>
  );
};

// ---------- Messages journal (Lot 17) ----------

const MessagesCard = () => {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [showTests, setShowTests] = useState(false);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      // Utilise le nouveau endpoint /journal avec filtres (Lot 18 finalisation)
      const params = { limit: 50 };
      if (showTests) params.test = true;
      if (status)    params.status = status;
      if (q)         params.q = q;
      const d = await api.platformListSmsJournal(params);
      setMessages(d.messages || []);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [showTests, status, q]);

  return (
    <section className="rounded-2xl border border-black/5 bg-white p-6" data-testid="platform-sms-messages">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[15px] font-semibold text-[#111]">Journal SMS · 50 derniers</h3>
        <label className="text-[12px] text-[#6B7280] inline-flex items-center gap-1.5">
          <input type="checkbox" checked={showTests}
                 onChange={(e) => setShowTests(e.target.checked)}
                 data-testid="filter-tests-only" />
          Tests uniquement
        </label>
      </div>

      <SmsJournalFilters status={status} q={q}
                          onStatus={setStatus} onQuery={setQ} />

      {loading ? (
        <Loader2 size={18} className="animate-spin text-[#6B7280]" />
      ) : messages.length === 0 ? (
        <p className="mt-6 text-center text-[13px] text-[#6B7280]">
          {q || status ? "Aucun résultat pour ce filtre." : "Aucun SMS pour le moment."}
        </p>
      ) : (
        <div className="mt-4 space-y-2">
          {messages.map((m) => (
            <div key={m.id} data-testid={`sms-row-${m.id}`}
                 className="rounded-xl border border-black/5 px-4 py-3">
              <div className="flex items-center gap-3">
                <StatusBadge status={m.status} />
                <span className="text-[13px] font-mono text-[#111]">{m.recipient}</span>
                <span className="text-[11px] text-[#6B7280] font-mono">
                  {m.segments} seg · {m.encoding}
                </span>
                {m.retries > 0 && (
                  <span className="text-[11px] text-[#B45309]">↻ {m.retries} retries</span>
                )}
                {m.test && (
                  <span className="text-[10px] uppercase tracking-widest bg-[#EDE9FE] text-[#5B21B6] px-1.5 py-0.5 rounded">
                    {m.test_kind || "test"}
                  </span>
                )}
                <span className="ml-auto text-[11px] text-[#6B7280]">
                  {new Date(m.created_at).toLocaleString("fr-FR")}
                </span>
              </div>
              {m.body_preview && (
                <p className="mt-1.5 text-[12.5px] text-[#374151] line-clamp-2">
                  {m.body_preview}
                </p>
              )}
              {m.error && (
                <p className="mt-1 text-[11px] text-[#B91C1C] font-mono break-all">{m.error}</p>
              )}
              <div className="mt-1 flex items-center gap-3 text-[10.5px] font-mono text-[#6B7280]">
                <span>event: {m.event_type}</span>
                {m.provider_message_id && <span>msg_id: {m.provider_message_id}</span>}
                {m.provider_status && <span>· sinch: {m.provider_status}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const StatusBadge = ({ status }) => {
  const cfg = {
    pending:   ["bg-[#F1F5F9] text-[#475569]", "En attente"],
    queued:    ["bg-[#DBEAFE] text-[#1E40AF]", "En file"],
    sent:      ["bg-[#DBEAFE] text-[#1D4ED8]", "Envoyé"],
    delivered: ["bg-[#D9FFE8] text-[#166534]", "Livré"],
    failed:    ["bg-[#FEE2E2] text-[#991B1B]", "Échec"],
    duplicate: ["bg-[#F3F4F6] text-[#374151]", "Dupliqué"],
    unknown:   ["bg-[#F1F5F9] text-[#475569]", status],
  }[status] || ["bg-[#F1F5F9] text-[#475569]", status];
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${cfg[0]}`}>
      {cfg[1]}
    </span>
  );
};

// ---------- Utils ----------

const KV = ({ k, v, testid }) => (
  <div>
    <p className="text-[10.5px] font-semibold uppercase tracking-wider text-[#6B7280]">{k}</p>
    <p data-testid={testid} className="mt-0.5 text-[13px] text-[#111] font-mono break-all">{v || "—"}</p>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[12px] font-semibold text-[#111]">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const ModalShell = ({ title, onClose, children, testId }) => (
  <div data-testid={testId} className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
       onClick={onClose}>
    <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl relative max-h-[92vh] overflow-y-auto"
         onClick={(e) => e.stopPropagation()}>
      <button onClick={onClose}
              className="absolute top-4 right-4 h-8 w-8 rounded-full flex items-center justify-center hover:bg-[#F7F8FA]"
              aria-label="Fermer">
        <X size={16} />
      </button>
      <h3 className="text-[17px] font-semibold text-[#111]">{title}</h3>
      {children}
    </div>
  </div>
);

export default PlatformSinchPage;
